import { ChangeEvent, FormEventHandler, MouseEventHandler, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import DatePicker, { registerLocale } from "react-datepicker";
import ja from "date-fns/locale/ja";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import { formatUtcToLocalDateTime } from "../utils/DateUtils";
import { getLicenseusers } from "../clients/LicenseClient";
import { Licenseuser } from "../types/Licenseuser";
import LicenseuserDetailModal from "./modals/LicenseuserDetailModal";
import PagenationButtons from "../components/PagenationButtons";
import { outputCsv, outputExcel } from "../utils/CsvUtils";
import { convertLicenseuserListForOutput } from "../utils/LicenseUtils";

type PackageOption = {
    id: number;
    label: string;
};

const packageOptions: PackageOption[] = [
    { id: 101, label: "DF Scanner Pro" },
    { id: 102, label: "DF Scanner Lite" },
    { id: 201, label: "DF LAT Pro" },
    { id: 202, label: "DF LAT Lite" },
    { id: 300, label: "DF BIRD" },
];

type MyDataPickerProps = {
    selectedDate: Date | null;
    setDate: (date: Date | null) => void;
    placeholderText: string;
};

/**
 * 管理者向け - ライセンス統計検索画面
 * @returns JSX.Element
 */
const ManagementLicenseStatistics = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getRecentToken } = useUserContext();
    registerLocale("ja", ja);

    const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([101, 102, 201, 202, 300]);
    const [insertDateStart, setInsertDateStart] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
    ); // 今月1日をデフォルト
    const [insertDateEnd, setInsertDateEnd] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1, 0, 0, 0, 0),
    ); // 来月1日をデフォルト
    const [expireDateStart, setExpireDateStart] = useState<Date | null>(null);
    const [expireDateEnd, setExpireDateEnd] = useState<Date | null>(null);
    const [onlyFreeTrial, setOnlyFreeTrial] = useState<boolean>(false);
    const [onlyProduct, setOnlyProduct] = useState<boolean>(false);
    const [onlyPermanent, setOnlyPermanent] = useState<boolean>(false);
    const [onlySubscription, setOnlySubscription] = useState<boolean>(false);
    const [excludeDeepForest, setExcludeDeepForest] = useState<boolean>(true);
    const [selectedLicense, setSelectedLicense] = useState<Licenseuser | undefined>(undefined);
    const [licenseList, setLicenseList] = useState<Licenseuser[]>([]);
    const [targetStatus, setTargetStatus] = useState<number>(0);
    const [sortStatus, setSortStatus] = useState<number>(0);
    const [doneFlg, setDoneFlg] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    // ページネーション
    const [licensePerPage, setLicensePerPage] = useState(10);
    const [licenceCurrentPage, setLicenseCurrentPage] = useState(1);
    const [licenseTotalPages, setLicenseTotalPages] = useState(1);

    // ライセンス情報詳細モーダル関連
    const [showLicenseDetailModal, setShowLicenseDetailModal] = useState<boolean>(false);
    const [errorMessageForLicenseDetail, setErrorMessageForLicenseDetail] = useState<string>("");

    /**
     * 処理中フラグ
     *
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    /**
     * 検索押下時のイベント処理
     * @param event
     */
    const handleSearchUser: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        setIsProcessing(true);
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API] ライセンスユーザー情報一括取得
            const request: { [name: string]: any } = {};
            if (selectedPackageIds.length == 0) {
                setErrorMessage("製品タイプを1つ以上指定してください");
                return;
            } else {
                request["package_ids"] = selectedPackageIds;
            }
            insertDateStart && (request["insert_date_start"] = insertDateStart.toISOString());
            insertDateEnd && (request["insert_date_end"] = insertDateEnd.toISOString());
            expireDateStart && (request["expire_date_start"] = expireDateStart.toISOString());
            expireDateEnd && (request["expire_date_end"] = expireDateEnd.toISOString());
            onlyFreeTrial != null && (request["only_free_trial"] = onlyFreeTrial);
            onlyProduct != null && (request["only_product"] = onlyProduct);
            onlyPermanent != null && (request["only_permanent"] = onlyPermanent);
            onlySubscription != null && (request["only_subscription"] = onlySubscription);
            excludeDeepForest != null && (request["exclude_deepforest"] = excludeDeepForest);

            const response: Licenseuser[] = await getLicenseusers(idToken, request);
            // 登録日時順でソート
            const sortedLicenseList: Licenseuser[] = response.sort((a, b) => {
                return new Date(b.insert_date).getTime() - new Date(a.insert_date).getTime();
            });
            setLicenseList(sortedLicenseList);
            // ページネーション用設定
            setLicenseTotalPages(Math.ceil(sortedLicenseList.length / licensePerPage));
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setIsProcessing(false);
            setDoneFlg(true);
        }
    };

    /**
     * 製品タイプ選択用トグル
     * @param id
     */
    const togglePackage = (id: number) => {
        setSelectedPackageIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    /**
     * 製品版/無料体験版 選択イベント
     * @param e
     */
    const handleChangeTarget = (e: ChangeEvent<HTMLSelectElement>) => {
        const status = parseInt(e.target.value);
        switch (status) {
            case 0: // すべて
                setOnlyProduct(false);
                setOnlyFreeTrial(false);
                setTargetStatus(0);
                break;
            case 1: // 製品版のみ
                setOnlyProduct(true);
                setOnlyFreeTrial(false);
                setTargetStatus(1);
                break;
            case 2: // 無料体験版のみ
                setOnlyProduct(false);
                setOnlyFreeTrial(true);
                setTargetStatus(2);
                break;
            default:
                setOnlyProduct(false);
                setOnlyFreeTrial(false);
                setTargetStatus(0);
                break;
        }
    };

    /**
     * 表示数設定 選択イベント
     * @param e
     */
    const handleChangeLicensePerPage = (e: ChangeEvent<HTMLSelectElement>) => {
        const licensePerPage = parseInt(e.target.value);
        setLicensePerPage(licensePerPage);
        setLicenseTotalPages(Math.ceil(licenseList.length / licensePerPage));
    };

    /**
     * ソート条件 選択イベント
     * @param e
     */
    const handleChangeSort = (e: ChangeEvent<HTMLSelectElement>) => {
        const status = parseInt(e.target.value);
        switch (status) {
            case 0: // 登録日時が新しい順
                setSortStatus(0);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return new Date(b.insert_date).getTime() - new Date(a.insert_date).getTime();
                    }),
                );
                break;
            case 1: // 登録日時が古い順
                setSortStatus(1);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return new Date(a.insert_date).getTime() - new Date(b.insert_date).getTime();
                    }),
                );
                break;
            case 2: // 有効期限が新しい順
                setSortStatus(2);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return new Date(b.expire_date).getTime() - new Date(a.expire_date).getTime();
                    }),
                );
                break;
            case 3: // 有効期限が古い順
                setSortStatus(3);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return new Date(a.expire_date).getTime() - new Date(b.expire_date).getTime();
                    }),
                );
                break;
            case 4: // 商品名順
                setSortStatus(4);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return a.package_name.localeCompare(b.package_name);
                    }),
                );
                break;
            case 5: // ライセンスタイプ順
                setSortStatus(5);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        const a_type = a.free_trial_flg
                            ? t("CommonLabel.freeTrialVersion")
                            : a.permanent_flg
                            ? t("CommonLabel.permanent")
                            : t("CommonLabel.subscribe");
                        const b_type = b.free_trial_flg
                            ? t("CommonLabel.freeTrialVersion")
                            : b.permanent_flg
                            ? t("CommonLabel.permanent")
                            : t("CommonLabel.subscribe");
                        return a_type.localeCompare(b_type);
                    }),
                );
                break;
            case 6: // 氏名順
                setSortStatus(6);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return `${a.family_name || ""}${a.given_name || ""}`.localeCompare(
                            `${b.family_name || ""}${b.given_name || ""}`,
                        );
                    }),
                );
                break;
            case 7: // 所属順
                setSortStatus(7);
                setLicenseList(
                    licenseList.sort((a, b) => {
                        return (a.organization || "").localeCompare(b.organization || "");
                    }),
                );
                break;
            default:
                break;
        }
    };

    /**
     * ライセンスリストのCSV/Excel出力押下時のイベント処理
     */
    const handleLicenseListOutput: MouseEventHandler<HTMLButtonElement> = async (event) => {
        const format = event.currentTarget.value;
        const headers = [
            { header: "ライセンスID", key: "license_id" },
            { header: "ライセンスキー", key: "license_key" },
            { header: "製品番号", key: "package_id" },
            { header: "製品名", key: "package_name" },
            { header: "利用開始日", key: "activation_date" },
            { header: "有効期限", key: "expire_date" },
            { header: "ユーザーID", key: "user_id" },
            { header: "ユーザーメールアドレス", key: "email" },
            { header: "ユーザー姓", key: "family_name" },
            { header: "ユーザー名", key: "given_name" },
            { header: "ユーザー所属", key: "organization" },
            { header: "グループID", key: "owner_group_id" },
            { header: "アクティベーションステータス", key: "activation_status" },
            { header: "アクティベーション日時", key: "activation_record_date" },
            { header: "SID", key: "sid" },
            { header: "コンピュータ名", key: "computer_name" },
            { header: "購入履歴ID", key: "payment_history_id" },
            { header: "無料体験フラグ", key: "free_trial_flg" },
            { header: "恒久版フラグ", key: "permanent_flg" },
            { header: "サポート開始日時", key: "support_start_date" },
            { header: "サポート有効期限日時", key: "support_expiration_date" },
            { header: "前回サポート有効期限日時", key: "last_support_expiration_date" },
            { header: "登録日時", key: "insert_date" },
            { header: "更新日時", key: "update_date" },
        ];
        const outputList: Licenseuser[] = convertLicenseuserListForOutput(licenseList);
        if (format === "csv") {
            outputCsv(headers, outputList, "Output");
        } else {
            outputExcel(headers, outputList, "Output");
        }
    };

    /**
     * [ライセンス詳細情報確認モーダル]
     * ライセンス詳細選択時のイベント処理
     * @param license 選択したライセンス情報
     */
    const handleLicenseDetail = (license: Licenseuser) => {
        setSelectedLicense(license);
        setErrorMessageForLicenseDetail("");
        setShowLicenseDetailModal(true);
    };

    /**
     * [ライセンス詳細情報確認モーダル]
     * キャンセル押下時のイベント処理
     */
    const handleCloseLicenseDetailModal = () => {
        setSelectedLicense(undefined);
        setShowLicenseDetailModal(false);
    };

    /**
     * 日時ピッカー共通コンポーネント
     * @param param0
     * @returns
     */
    const MyDatePicker: React.FC<MyDataPickerProps> = ({ selectedDate, setDate, placeholderText }) => {
        return (
            <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => {
                    setDate(date);
                }}
                disabled={false}
                showTimeSelect
                placeholderText={placeholderText}
                className="mb-1 rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900"
                dateFormat={i18n.language === "ja" ? "yyyy/MM/dd HH:mm" : "dd/MM/yyyy hh:mm a"}
                locale={i18n.language === "ja" ? "ja" : undefined}
            />
        );
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title="ライセンス統計 | DF WebService" />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">ライセンス統計</h1>
                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}
                <form className="mx-auto max-w-4xl" onSubmit={handleSearchUser}>
                    <div className="grid grid-cols-2 items-start justify-center gap-2 pb-2 sm:m-2 md:grid-cols-4">
                        {/* 製品タイプ */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">■ 製品タイプ</div>
                            <div className="flex flex-col">
                                {packageOptions.map((pkg) => (
                                    <label key={pkg.id} className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            value={pkg.id}
                                            checked={selectedPackageIds.includes(pkg.id)}
                                            onChange={() => togglePackage(pkg.id)}
                                        />
                                        {pkg.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                        {/* 製品版 / 無料体験 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">■ 製品版 / 無料体験</div>
                            <select
                                id="selectTarget"
                                value={targetStatus}
                                onChange={handleChangeTarget}
                                className="w-[150px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                            >
                                <option value={0}>すべて</option>
                                <option value={1}>製品版のみ</option>
                                <option value={2}>無料体験のみ</option>
                            </select>
                        </div>
                        {/* ライセンス発行日時 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">■ ライセンス発行日時</div>
                            <MyDatePicker
                                selectedDate={insertDateStart}
                                setDate={setInsertDateStart}
                                placeholderText="開始日時"
                            />
                            <MyDatePicker
                                selectedDate={insertDateEnd}
                                setDate={setInsertDateEnd}
                                placeholderText="終了日時"
                            />
                        </div>
                        {/* ライセンス有効期限 */}
                        <div className="col-span-1 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">■ ライセンス有効期限</div>
                            <MyDatePicker
                                selectedDate={expireDateStart}
                                setDate={setExpireDateStart}
                                placeholderText="開始日時"
                            />
                            <MyDatePicker
                                selectedDate={expireDateEnd}
                                setDate={setExpireDateEnd}
                                placeholderText="終了日時"
                            />
                        </div>
                        {/* その他 */}
                        <div className="col-span-2 py-1">
                            <div className="py-2 text-sm font-bold text-gray-800">■ その他オプション</div>
                            <div className="flex flex-col">
                                <label className="inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        className="mr-2"
                                        checked={excludeDeepForest}
                                        onChange={(e) => setExcludeDeepForest(e.target.checked)}
                                    />
                                    *@deepforest-tech.co.jpを除外する
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* 検索ボタン */}
                    <div className="flex items-center justify-evenly">
                        <button
                            type="submit"
                            className={`${isProcessing ? "btn-primary-disabled" : "btn-primary"} w-[150px] py-2`}
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center">
                                    <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                    <label className="">Processing...</label>
                                </div>
                            ) : (
                                t("CommonLabel.search")
                            )}
                        </button>
                    </div>
                </form>
                <hr className="my-5 border-b border-t-2" />
                {licenseList.length === 0 ? (
                    // ライセンスが存在しない場合
                    <div className="flex items-center justify-center py-4 font-bold text-gray-700">
                        {doneFlg ? "ライセンスが見つかりませんでした。" : "条件を指定して検索してください。"}
                    </div>
                ) : (
                    // ライセンスが見つかった場合
                    <div className="mx-auto max-w-6xl overflow-x-auto">
                        <div className="grid grid-cols-1 items-start justify-center md:grid-cols-3">
                            <div className="col-span-1 flex items-center justify-center pb-4 font-bold text-gray-700 md:col-span-3">
                                {licenseList.length}件のライセンスが見つかりました。
                            </div>
                            <div className="col-span-1"></div>
                            <div className="col-span-1 flex gap-3 md:items-end md:justify-end">
                                {/* 表示数 */}
                                <div className="mb-1 flex items-center justify-evenly">
                                    <select
                                        id="selectLicensePerPage"
                                        value={licensePerPage}
                                        onChange={handleChangeLicensePerPage}
                                        className="w-[150px] rounded-md border border-gray-300 bg-gray-50 p-1 text-sm text-gray-700"
                                    >
                                        <option value={10}>表示数：10</option>
                                        <option value={20}>表示数：20</option>
                                        <option value={50}>表示数：50</option>
                                        <option value={100}>表示数：100</option>
                                    </select>
                                </div>
                                {/* ソート条件 */}
                                <div className="mb-1 flex items-center justify-evenly">
                                    <select
                                        id="selectSort"
                                        value={sortStatus}
                                        onChange={handleChangeSort}
                                        className="w-[250px] rounded-md border border-gray-300 bg-gray-50 p-1 text-sm text-gray-700"
                                    >
                                        <option value={0}>発行日時が新しい順</option>
                                        <option value={1}>発行日時が古い順</option>
                                        <option value={2}>有効期限が新しい順</option>
                                        <option value={3}>有効期限が古い順</option>
                                        <option value={4}>商品名順</option>
                                        <option value={5}>ライセンスタイプ順</option>
                                        <option value={6}>氏名順</option>
                                        <option value={7}>所属順</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-span-1 flex gap-3 md:items-end md:justify-end">
                                {/* CSV出力ボタン */}
                                <div className="mb-1 flex items-center justify-evenly">
                                    <button
                                        type="button"
                                        onClick={handleLicenseListOutput}
                                        value="csv"
                                        className={`${
                                            licenseList.length === 0 ? "" : "hover:bg-lime-300 active:bg-lime-500"
                                        } w-[160px] rounded-lg border border-gray-200 bg-lime-100 py-1 text-center text-sm font-medium text-gray-700`}
                                        disabled={licenseList.length === 0}
                                    >
                                        {t("CommonLabel.csvOutput")}
                                    </button>
                                </div>
                                {/* Excel出力ボタン */}
                                <div className="mb-1 flex items-center justify-evenly">
                                    <button
                                        type="button"
                                        onClick={handleLicenseListOutput}
                                        value="excel"
                                        className={`${
                                            licenseList.length === 0 ? "" : "hover:bg-lime-300 active:bg-lime-500"
                                        } w-[160px] rounded-lg border border-gray-200 bg-lime-100 py-1 text-center text-sm font-medium text-gray-700`}
                                        disabled={licenseList.length === 0}
                                    >
                                        {t("CommonLabel.excelOutput")}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <table className="mb-3 min-w-full divide-y divide-gray-200 border">
                            <thead className="bg-gray-400">
                                <tr
                                    className={`${
                                        i18n.language === "ja" ? "text-base" : "text-sm"
                                    } font-semibold uppercase tracking-wide text-gray-100`}
                                >
                                    {/* [ヘッダ] 製品名 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.productName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] ライセンスタイプ */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.licenseType")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 氏名 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.fullName")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 所属 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.organization")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 発行日時 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.issueDate")}</span>
                                        </div>
                                    </th>
                                    {/* [ヘッダ] 有効期限 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left">
                                        <div className="flex items-center gap-x-2">
                                            <span>{t("CommonLabel.expirationDate")}</span>
                                        </div>
                                    </th>
                                    {/* ライセンス詳細 */}
                                    <th scope="col" className="whitespace-nowrap px-4 py-1 text-left"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {licenseList
                                    .slice(
                                        (licenceCurrentPage - 1) * licensePerPage,
                                        (licenceCurrentPage - 1) * licensePerPage + licensePerPage,
                                    )
                                    .map((l) => (
                                        <tr key={l.license_id} className="text-sm font-semibold text-gray-800">
                                            {/* 製品名 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-4 py-2">
                                                    <span>{l.package_name}</span>
                                                </div>
                                            </td>
                                            {/* ライセンスタイプ */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-4 py-2">
                                                    <span>
                                                        {l.free_trial_flg
                                                            ? t("CommonLabel.freeTrialVersion")
                                                            : l.permanent_flg
                                                            ? t("CommonLabel.permanent")
                                                            : t("CommonLabel.subscribe")}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* 氏名 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="max-w-[150px] px-4 py-2">
                                                    <span className="block overflow-hidden truncate whitespace-nowrap">
                                                        {l.family_name + " " + l.given_name}
                                                    </span>
                                                </div>
                                            </td>
                                            {/*　所属 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="max-w-[150px] px-4 py-2">
                                                    <span className="block overflow-hidden truncate whitespace-nowrap">
                                                        {l.organization}
                                                    </span>
                                                </div>
                                            </td>
                                            {/* 発行日時 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-4 py-2">
                                                    {formatUtcToLocalDateTime(l.insert_date)}
                                                </div>
                                            </td>
                                            {/* 有効期限 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-4 py-2">
                                                    {l.permanent_flg ? (
                                                        <span>-</span>
                                                    ) : (
                                                        <span>{formatUtcToLocalDateTime(l.expire_date)}</span>
                                                    )}
                                                </div>
                                            </td>
                                            {/* 詳細を確認 */}
                                            <td className="h-px w-px whitespace-nowrap">
                                                <div className="px-2 py-1.5">
                                                    <button
                                                        className="text-sm font-medium text-blue-700 hover:underline"
                                                        onClick={() => handleLicenseDetail(l)}
                                                    >
                                                        詳細を確認
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        {/* ページネーションコントロール */}
                        <PagenationButtons
                            currentPage={licenceCurrentPage}
                            setCurrentPage={setLicenseCurrentPage}
                            totalPages={licenseTotalPages}
                        />
                    </div>
                )}
                {/* 戻るボタン */}
                <div className="mx-auto flex max-w-xl items-center justify-evenly py-10 ">
                    <button
                        type="button"
                        onClick={() => navigate(MYPAGE_ROUTE)}
                        className="btn-secondary mx-2 w-[100px]"
                        disabled={isProcessing}
                    >
                        {t("CommonLabel.back")}
                    </button>
                </div>
            </div>
            {/* ライセンス詳細情報確認モーダル */}
            <LicenseuserDetailModal
                showModal={showLicenseDetailModal}
                selectedLicense={selectedLicense}
                handleClose={handleCloseLicenseDetailModal}
                errorMessage={errorMessageForLicenseDetail}
            />
            <Footer />
        </div>
    );
};
export default ManagementLicenseStatistics;
